import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Container, Typography, CircularProgress, Box, Button, RadioGroup, FormControlLabel, Radio,
    Checkbox, TextField, FormControl, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Layout from '../Layout';
import axiosInstance from '../../axiosConfig';
import { colors } from '../../colors';
import Slider from '@mui/material/Slider';
import InactivityDialog from '../survey/InactivityDialog';
import SurveyInactiveDialog from '../survey/SurveyInactiveDialog';
import SurveyCompletedDialog from '../survey/SurveyCompletedDialog';
import ConsentDialog from '../survey/ConsentDialog';
import SelfDescribeDialog from '../survey/SelfDescribeDialog';
import SurveyHeader from '../survey/SurveyHeader';
import NavigationPane from '../survey/NavigationPane';
import MobileSurveyLayout from '../survey/MobileSurveyLayout';
import { MenuItem } from '@mui/material';

const PreviewSurvey = () => {
    const { surveyId } = useParams();
    const [isMobileAllowed, setIsMobileAllowed] = useState(true);
    const [isMobile, setIsMobile] = useState(/Mobi|Android/i.test(navigator.userAgent));
    const [survey, setSurvey] = useState(null);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(null);
    const [responses, setResponses] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [consentOpen, setConsentOpen] = useState(false);
    const [surveyComplete, setSurveyComplete] = useState(false);
    const [surveyInactive, setSurveyInactive] = useState(false);
    const [universityName, setUniversityName] = useState('');
    const [selfDescribeOpen, setSelfDescribeOpen] = useState(false);
    const [selfDescribeInput, setSelfDescribeInput] = useState('');
    const [selfDescribeQuestion, setSelfDescribeQuestion] = useState(null);
    const [variables, setVariables] = useState([]);
    const [isLastSection, setIsLastSection] = useState(false); // Add flag for last section
    const [comment, setComment] = useState('');
    const [iCmtEnabled, setICmtEnabled] = useState(true);
    const [isInactive, setIsInactive] = useState(false); // Tracks inactivity
    const [inactivityTime, setInactivityTime] = useState(0); // Tracks inactivity duration
    const [totalSections, setTotalSections] = useState(0); // Total sections
    const [sectionNames, setSectionNames] = useState([]);
    const participantId = 'preview';

    useEffect(() => {
        const fetchSurvey = async () => {
            try {
                const response = await axiosInstance.get(`/preview/surveys/${surveyId}/${surveyId}`);
                const data = response.data;

                if (!data.active) {
                    setSurveyInactive(true);  // Survey is inactive
                } else if (data.completed || !data.sections) {
                    setSurveyComplete(true);  // Survey already completed
                    setICmtEnabled(!data.iCmt);
                } else {
                    // Set all necessary states
                    setSurvey(data);
                    setVariables(data.variables || []);
                    setUniversityName(data.universityName);
                    setCurrentSectionIndex(data.sections[0].sectionIndex);
                    setTotalSections(data.sectionSize);
                    setSectionNames(data.sectionNames || []);
                    setConsentOpen(data.consentNeeded);
                    setResponses(data.responses || {});
                    setIsLastSection(data.lastSection || false);
                }
            } catch (error) {
                console.error('Error fetching survey data:', error);
                setError('Survey not found or an error occurred');
            } finally {
                setLoading(false);  // Stop loading in all cases
            }
        };

        fetchSurvey();

        // Inactivity tracking initialization
        const cleanupInactivity = initializeInactivityTracking();

        return cleanupInactivity;
    }, [surveyId, participantId, axiosInstance, isMobile]);

    // Helper function to handle inactivity tracking
    const initializeInactivityTracking = () => {
        let timer, interval;

        const resetTimer = () => {
            setIsInactive(false);
            setInactivityTime(0);
            clearTimeout(timer);
            clearInterval(interval);
            timer = setTimeout(() => {
                setIsInactive(true);
                interval = setInterval(() => setInactivityTime(prev => prev + 1), 60000);
            }, 240000);  // 4 minutes
        };

        const handleActivity = () => resetTimer();

        // Attach listeners and reset timer
        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keypress', handleActivity);
        window.addEventListener('click', handleActivity);
        resetTimer();

        // Cleanup function
        return () => {
            clearTimeout(timer);
            clearInterval(interval);
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keypress', handleActivity);
            window.removeEventListener('click', handleActivity);
        };
    };


    // Show restriction message if mobile access is not allowed
    if (!isMobileAllowed) {
        console.log('You are using mobile: ', isMobile);
        return (
            <div style={{ textAlign: 'center', marginTop: '20%', fontFamily: 'Arial, sans-serif' }}>
                <h2 style={{ color: '#1E2742' }}>Access Restricted</h2>
                <p>{error}</p>
            </div>
        );
    }


    const calculateProgress = () => {
        if (totalSections === 0) return 0; // Avoid division by zero
        console.log('----------', ((currentSectionIndex) / totalSections) * 100);
        return ((currentSectionIndex) / totalSections) * 100;
    };

    // Function to handle comment submission
    const handleCommentSubmit = async () => {
        try {
            await axiosInstance.post(`/preview/surveys/${surveyId}/${surveyId}/comment`, { comment });
            alert('Thank you for your feedback!');
            setComment(''); // Reset comment after submission
            window.location.reload();
        } catch (error) {
            console.error('Error submitting comment:', error);
            alert('Failed to submit comment. Please try again.');
        }
    };

    const shouldDisplayQuestion = (question) => {
        // If there is no condition, always display the question
        if (!question.condition) {
            return true;
        }

        // Extract condition details
        const { questionId, expectedAnswer } = question.condition;

        // Check if the response for the condition question matches the expected answer
        return responses[questionId] === expectedAnswer;
    };


    const replacePlaceholders = (questionId, questionText, variables = []) => {
        if (!questionText) return questionText;

        const symbol = '•';

        let formattedText = questionText;
        variables.forEach(variable => {
            const placeholder = `<${variable.key}>`;
            formattedText = formattedText.replaceAll(placeholder, variable.value);
        });

        const finalText = formattedText
            .split('###')
            .map((line, index, arr) => {
                if (index === arr.length - 1 && arr.length > 1) {
                    return (
                        <span key={`signature-${index}`} style={{ display: 'block', textAlign: 'right', fontStyle: 'italic', marginTop: '10px' }}>
                            {line.trim()}
                        </span>
                    );
                }

                return line.split('##').map((subLine, subIndex) => (
                    subLine.split('#').map((innerLine, innerIndex) => (
                        <span key={`${index}-${subIndex}-${innerIndex}`} style={{ display: 'block', marginBottom: '5px' }}>
                            <span style={{ fontSize: '1.5em', marginRight: '5px' }}>{symbol}</span>
                            {innerLine.trim()}
                        </span>
                    ))
                ));
            })
            .flat();

        return finalText;
    };

    const handleResponseChange = (questionId, value, questionText = '') => {
        console.log('Checking....:', questionId);
        console.log('Checking Value....:', value);
    
        // Handle both table and non-table questions
        if (
            // Check if value contains phrases like "Prefer to self-describe" or "please specify"
            value.includes('Prefer to self-describe') ||
            value.includes('Other, please specify') ||
            value.includes('Other, please, specify') ||
            value.includes('please specify') ||
    
            // Check if response is "Yes" and question text has "please specify" condition
            (value === 'Yes' && (
                questionText.includes('Prefer to self-describe') ||
                questionText.includes('Other, please specify') ||
                questionText.includes('Other, please, specify') ||
                questionText.includes('please specify')
            ))
        ) {
            console.log('setSelfDescribeQuestion:', questionId);
            setSelfDescribeQuestion(questionId);
            setSelfDescribeOpen(true);
        } else {
            // Save the response normally
            setResponses((prev) => ({
                ...prev,
                [questionId]: value,
            }));
        }
    };
    

    const handleSelfDescribeClose = () => {
        if (selfDescribeInput.trim()) {
            setResponses((prev) => {
                const updatedResponses = {
                    ...prev,
                    [selfDescribeQuestion]: selfDescribeInput,
                };
                return updatedResponses;
            });
        }
        setSelfDescribeOpen(false);
        setSelfDescribeInput('');
    };

    const getQuestionText = () => {
        if (!selfDescribeQuestion || !survey?.sections) return '';
        const section = survey.sections.find((section) =>
            section.questions.some((q) => q.questionId === selfDescribeQuestion)
        );
        const question = section?.questions.find((q) => q.questionId === selfDescribeQuestion);
        return question?.questionText || '';
    };

    const handlePreviousSection = async () => {
        if (currentSectionIndex > 0) {
            try {
                // Fetch the previous section and its responses from backend
                const response = await axiosInstance.get(`/preview/surveys/${surveyId}/${surveyId}/section/${currentSectionIndex - 1}`);

                if (response.data) {
                    const previousSection = response.data.sections.find(section => section.sectionIndex === currentSectionIndex - 1);
                    setSurvey((prevSurvey) => ({
                        ...prevSurvey,
                        sections: response.data.sections,
                    }));
                    setResponses(response.data.responses || {});
                    setCurrentSectionIndex(previousSection.sectionIndex);
                    setIsLastSection(false);  // Reset the last section flag when going back

                    // Update sectionNames for navigation pane
                    setSectionNames(response.data.sectionNames || []);
                }
            } catch (error) {
                console.error('Error fetching previous section:', error);
            }
        }
    };

    const handleSectionClick = async (sectionIndex) => {
        if (sectionIndex === currentSectionIndex) return; // Avoid unnecessary calls for the current section

        try {
            // Save current section responses before navigating
            const responseDto = {
                sectionIndex: currentSectionIndex,
                responses: responses,
            };
            await axiosInstance.post(`/preview/surveys/${surveyId}/${participantId}/submit`, responseDto);

            // Fetch data for the selected section
            const response = await axiosInstance.get(
                `/preview/surveys/${surveyId}/${surveyId}/section/${sectionIndex}`
            );

            if (response.data) {
                setSurvey((prevSurvey) => ({
                    ...prevSurvey,
                    sections: response.data.sections,
                }));
                setResponses(response.data.responses || {});
                setCurrentSectionIndex(sectionIndex);
                // Update sectionNames for navigation pane
                setSectionNames(response.data.sectionNames || []);
            }
        } catch (error) {
            console.error('Error fetching section data:', error);
            alert('Failed to load section. Please try again.');
        }
    };

    const handleNextSection = async () => {
        const responseDto = {
            sectionIndex: currentSectionIndex,
            responses: responses,
        };

        try {
            const response = await axiosInstance.post(
                `/preview/surveys/${surveyId}/${surveyId}/submit`,
                responseDto
            );

            if (response.data.sections) {
                const nextSection = response.data.sections.find(section => section.sectionIndex === currentSectionIndex + 1);
                setSurvey((prevSurvey) => ({
                    ...prevSurvey,
                    sections: response.data.sections,
                }));
                const nextSectionResponses = response.data.responses || {};
                setResponses(nextSectionResponses);
                setCurrentSectionIndex(nextSection.sectionIndex);
                setIsLastSection(response.data.lastSection || false);  // Update the last section flag based on backend response
                // Update sectionNames for navigation pane
                setSectionNames(response.data.sectionNames || []);
            } else {
                setSurveyComplete(true);
            }
        } catch (error) {
            console.error('Error submitting section:', error);
        }
    };

    const handleCloseSurvey = () => {
        if (window.opener) {
            window.close();
        } else {
            window.open('', '_self', '');
            window.close();
        }
    };

    const handleConsent = (consent) => {
        if (consent) {
            setConsentOpen(false);
        } else {
            alert('Survey cannot proceed without consent.');
        }
    };

    const renderSCQ = (question) => (
        shouldDisplayQuestion(question) && (
            <Box sx={{ mb: 2 }}>
                <RadioGroup
                    name={`question-${question.questionId}`}
                    value={responses[question.questionId] || ''}  // Prepopulate response if available
                    onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
                    aria-label={question.questionText}  // Accessibility improvement
                >
                    {question.options.map((option, index) => (
                        <FormControlLabel
                            key={index}
                            value={option}
                            control={<Radio />}
                            label={option}
                        />
                    ))}
                </RadioGroup>

                {/* Display custom response if it doesn't match any of the predefined options */}
                {!question.options.some(option => option.trim().toLowerCase() === (responses[question.questionId] || '').trim().toLowerCase()) &&
                    responses[question.questionId] && (
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 1,
                                padding: '4px 8px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontStyle: 'italic',
                                color: '#555',
                            }}
                        >
                            {responses[question.questionId]}
                        </Typography>
                    )
                }
            </Box>
        )
    );


    const renderSCQWithEnhancedSlider = (question) => {
        const minValue = 0;
        const maxValue = question.options.length - 1;

        // Get the index of the currently selected response
        const getSelectedValueIndex = () => {
            const selectedValue = responses[question.questionId];
            return question.options.findIndex((option) => option === selectedValue);
        };

        // Handle slider value change
        const handleSliderChange = (event, value) => {
            handleResponseChange(question.questionId, question.options[value]);
        };

        // Fallback gradient colors if theme colors are missing
        const gradient = `linear-gradient(90deg, ${colors.racineRedDark || '#bc2534'}, ${colors.racineRedLight || '#ff6961'})`;

        return (
            shouldDisplayQuestion(question) && (
                <Box sx={{ mb: 5 }}>
                    {/* Question Text */}
                    <Typography
                        variant="h6"
                        sx={{
                            mb: 4,
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                        }}
                        id={`question-text-${question.questionId}`}
                    >
                        {question.text}
                    </Typography>

                    {/* Slider Container */}
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            px: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {/* Slider */}
                        <Box sx={{ width: '80%' }}>
                            <Slider
                                value={Math.max(getSelectedValueIndex(), minValue)}  // Ensure a valid index
                                min={minValue}
                                max={maxValue}
                                step={1}
                                onChange={handleSliderChange}
                                marks={question.options.map((option, index) => ({
                                    value: index,
                                    label: option, // Show options directly on marks
                                }))}
                                valueLabelDisplay="off"
                                sx={{
                                    '& .MuiSlider-track': {
                                        background: gradient,
                                        height: '6px',
                                        border: 'none',
                                    },
                                    '& .MuiSlider-thumb': {
                                        background: gradient,
                                        width: '24px',
                                        height: '24px',
                                        border: '2px solid white',
                                        '&:hover, &.Mui-focusVisible': {
                                            boxShadow: `0 0 0 8px rgba(255, 105, 97, 0.2)`,
                                        },
                                    },
                                    '& .MuiSlider-rail': {
                                        backgroundColor: '#e0e0e0',
                                        opacity: 1,
                                    },
                                    '& .MuiSlider-mark': {
                                        height: '8px',
                                        width: '8px',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '50%',
                                    },
                                    '& .MuiSlider-markActive': {
                                        background: gradient,
                                    },
                                }}
                                aria-labelledby={`question-text-${question.questionId}`}
                            />
                        </Box>
                    </Box>
                </Box>
            )
        );
    };


    const renderMCQ = (question) => (
        shouldDisplayQuestion(question) && (
            <FormControl component="fieldset">
                {question.options.map((option, index) => (
                    <FormControlLabel
                        key={index}
                        control={
                            <Checkbox
                                checked={responses[question.questionId]?.includes(option) || false}
                                onChange={(e) => {
                                    const value = responses[question.questionId] || [];
                                    if (e.target.checked) {
                                        handleResponseChange(question.questionId, [...value, option]);
                                    } else {
                                        handleResponseChange(
                                            question.questionId,
                                            value.filter((v) => v !== option)
                                        );
                                    }
                                }}
                            />
                        }
                        label={option}
                    />
                ))}
            </FormControl>
        )
    );

    const renderDropdown = (question) => (
        shouldDisplayQuestion(question) && (
            <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField
                    select
                    label={question.text}  // Add label for clarity
                    value={responses[question.questionId] || ''}
                    onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
                    SelectProps={{
                        native: false,  // Use non-native for more customization
                    }}
                    variant="outlined"
                >
                    {/* Placeholder option */}
                    <MenuItem value="" disabled>
                        Please select an option
                    </MenuItem>
                    {question.options.map((option, index) => (
                        <MenuItem key={index} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
            </FormControl>
        )
    );

    const renderInput = (question) => (
        shouldDisplayQuestion(question) && (
            <TextField
                fullWidth
                label={question.text}           // Display question text as the label
                value={responses[question.questionId] || ''}
                onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
                placeholder="Enter your response"  // Optional placeholder
                variant="outlined"              // Consistent styling with other components
                sx={{ mt: 2 }}
                aria-labelledby={`question-${question.questionId}`}  // Accessibility improvement
            />
        )
    );


    const renderTableQuestions = (question) => {
        if (!shouldDisplayQuestion(question)) return null;

        const subQuestions = question.subQuestions || [];
        const tableOptions = question.options || [];

        // Group options by header value
        const groupedHeaders = [];
        let currentGroup = { header: tableOptions[0], count: 0 };

        tableOptions.forEach((option) => {
            if (option === currentGroup.header) {
                currentGroup.count += 1;
            } else {
                groupedHeaders.push(currentGroup);
                currentGroup = { header: option, count: 1 };
            }
        });
        groupedHeaders.push(currentGroup);  // Push the last group

        return (
            <Box
                key={question.questionId}
                sx={{
                    mt: 3,
                    mb: 3,
                    p: 3,
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    borderLeft: `4px solid ${colors.racineRedDark}`,
                }}
            >
                {/* Table Header */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {replacePlaceholders(question.questionId, question.questionText, variables)}
                </Typography>

                <Table
                    sx={{
                        backgroundColor: '#f9f9f9',
                        tableLayout: 'fixed',
                        width: '100%',
                        borderCollapse: 'collapse',
                    }}
                    size="small"
                >
                    <TableHead>
                        <TableRow
                            sx={{
                                borderBottom: `2px solid ${colors.racineRedDark}`,  // Apply bottom border to the entire row
                                height: '30px',
                            }}
                        >
                            {/* Main Question Header */}
                            <TableCell
                                sx={{
                                    fontWeight: 'bold',
                                    color: colors.racineRedDark,
                                    width: '40%',
                                    textAlign: 'left',
                                    whiteSpace: 'normal',
                                    padding: '6px',
                                    borderRight: groupedHeaders.length > 0 ? '1px solid #ddd' : 'none',  // Vertical line only if headers exist
                                }}
                            >
                                Question
                            </TableCell>

                            {/* Grouped Headers */}
                            {groupedHeaders.length > 0 ? (
                                groupedHeaders.map((group, index) => (
                                    <TableCell
                                        key={index}
                                        sx={{
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            fontSize: '.75rem',
                                            color: colors.racineRedDark,
                                            padding: '6px',
                                            borderRight: index < groupedHeaders.length - 1 ? '1px solid #ddd' : 'none',  // Vertical line between grouped headers
                                        }}
                                        colSpan={group.count}
                                    >
                                        {group.header}
                                    </TableCell>
                                ))
                            ) : (
                                <TableCell
                                    sx={{
                                        textAlign: 'center',
                                        padding: '10px',
                                    }}
                                    colSpan={1}  // Adjust this if needed for proper alignment
                                >
                                    &nbsp;  {/* Empty placeholder for consistent layout */}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {subQuestions.map((subQuestion, subIndex) => (
                            <TableRow key={subIndex}>
                                {/* Sub-question text */}
                                <TableCell
                                    sx={{
                                        padding: '10px',
                                        color: '#000',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        borderRight: '1px solid #ddd',
                                    }}
                                >
                                    {replacePlaceholders(subQuestion.subQuestionId, subQuestion.subQuestionText, variables)}
                                </TableCell>

                                {/* Render each sub-question's options */}
                                {subQuestion.options.map((option, optionIndex) => (
                                    <TableCell
                                        key={optionIndex}
                                        sx={{
                                            textAlign: 'center',
                                            borderRight: optionIndex < subQuestion.options.length - 1 ? '1px solid #ddd' : 'none',
                                        }}
                                    >
                                        {/* Radio button and option */}
                                        <Radio
                                            checked={responses[subQuestion.subQuestionId] === option}
                                            onChange={() => handleResponseChange(subQuestion.subQuestionId, option, subQuestion.subQuestionText)}
                                        />
                                        <Typography variant="body2">{option}</Typography>

                                        {/* Display custom response below the Radio if it's not one of the options */}
                                        {optionIndex === 0 &&  // Only render custom response once per row under the first column with options
                                            !subQuestion.options.some(
                                                (opt) => opt.trim().toLowerCase() === (responses[subQuestion.subQuestionId] || '').trim().toLowerCase()
                                            ) &&
                                            responses[subQuestion.subQuestionId] && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        mt: 1,
                                                        padding: '4px 8px',
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ddd',
                                                        fontStyle: 'italic',
                                                        color: '#555',
                                                    }}
                                                >
                                                    {responses[subQuestion.subQuestionId]}
                                                </Typography>
                                            )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}

                    </TableBody>
                </Table>
            </Box>
        );
    };



    const pageVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
    };

    const sectionVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0 },
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading Survey...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography>{error}</Typography>;
    }


    return (
        <>
            {isMobile ? <MobileSurveyLayout surveyId={surveyId} participantId={participantId} />
                :
                <Layout>
                    {/* Blue Banner */}
                    <Box
                        sx={{
                            backgroundColor: colors.primaryBannerBlue,
                            color: 'white',
                            padding: { xs: '5px 0', sm: '10px 0' }, // Responsive padding
                            textAlign: 'center',
                            fontSize: { xs: '1rem', sm: '1.2rem' },
                            fontWeight: '500',
                        }}
                    >
                        Your opinion matters!
                    </Box>
                    <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' }, // Stack vertically on small screens, side-by-side on large screens
                                width: '100%',
                                minHeight: '100vh',
                                backgroundColor: '#eaecee',
                                padding: { xs: '0px', sm: '0px', md: '10px' },
                                boxSizing: 'border-box', // Ensures padding is included in total width/height
                                gap: { xs: 2, sm: 3 }, // Consistent gap between sections
                            }}
                        >
                            {/* Navigation Pane */}
                            <NavigationPane
                                sectionNames={sectionNames} // Updated to use `sectionNames`
                                currentSectionIndex={currentSectionIndex}
                                onSectionClick={handleSectionClick}
                            />
                            {/* Main Survey Content */}
                            <Box
                                sx={{
                                    flexGrow: 1, // Take up remaining space
                                    padding: '15px',
                                    backgroundColor: 'white',
                                    borderRadius: '20px',
                                    boxShadow: '0 6px 14px rgba(0, 0, 0, 0.1)',
                                }}
                            >
                                {!surveyInactive && !surveyComplete && !consentOpen && survey && (
                                    <>
                                        {/* Survey Header */}
                                        <SurveyHeader
                                            surveyName={survey.surveyName}
                                            surveyDescription={survey.surveyDescription}
                                            logoSrc="/racine.png"
                                            colors={colors}
                                        />
                                        {/* Survey Sections */}
                                        {survey.sections.map((section, sectionIndex) => (
                                            <motion.div
                                                key={sectionIndex}
                                                initial="hidden"
                                                animate="visible"
                                                variants={sectionVariants}
                                                transition={{ duration: 0.4, delay: sectionIndex * 0.2 }}
                                                style={{ marginBottom: '40px' }}
                                            >
                                                {/* Section Title */}
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        mt: 4,
                                                        mb: 3,
                                                        fontWeight: 'bold',
                                                        color: colors.racineRedDark,
                                                        borderBottom: `2px solid ${colors.racineRedDark}`,
                                                        paddingBottom: '10px',
                                                    }}
                                                >
                                                    {section.sectionTitle}
                                                </Typography>

                                                {section.questions.map((question, questionIndex) => {
                                                    if (!shouldDisplayQuestion(question)) return null;

                                                    const questionVariants = {
                                                        hidden: { opacity: 0, y: 10 },
                                                        visible: { opacity: 1, y: 0 },
                                                    };

                                                    // Render table questions with sub-questions
                                                    if (question.questionType === 'TABLE') {
                                                        return (
                                                            <motion.div
                                                                key={questionIndex}
                                                                variants={questionVariants}
                                                                initial="hidden"
                                                                animate="visible"
                                                                transition={{ duration: 0.5 }}
                                                                style={{ marginBottom: '30px' }}
                                                            >
                                                                {renderTableQuestions(question)}
                                                            </motion.div>
                                                        );
                                                    }

                                                    // Render quote questions
                                                    if (question.questionType === 'QUOTE') {
                                                        return (
                                                            <motion.div
                                                                key={questionIndex}
                                                                variants={questionVariants}
                                                                initial="hidden"
                                                                animate="visible"
                                                                transition={{ duration: 0.3 }}
                                                                style={{
                                                                    margin: '20px 0',
                                                                    padding: '10px 20px',
                                                                    borderLeft: `4px solid ${colors.racineRedDark}`,
                                                                    fontStyle: 'italic',
                                                                    backgroundColor: '#f9f9f9',
                                                                    borderRadius: '8px',
                                                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
                                                                }}
                                                            >
                                                                <Typography variant="body1" sx={{ color: colors.primaryApplicationText }}>
                                                                    {replacePlaceholders("", question.questionText, variables)}
                                                                </Typography>
                                                            </motion.div>
                                                        );
                                                    }

                                                    // Render other question types
                                                    return (
                                                        <motion.div
                                                            key={questionIndex}
                                                            variants={questionVariants}
                                                            initial="hidden"
                                                            animate="visible"
                                                            transition={{ duration: 0.5 }}
                                                            style={{
                                                                margin: '30px 0',
                                                                padding: '20px',
                                                                backgroundColor: '#ffffff',
                                                                borderRadius: '8px',
                                                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                                                borderLeft: `4px solid ${colors.racineRedDark}`,
                                                                lineHeight: '1.7',
                                                            }}
                                                        >
                                                            {/* Question Text */}
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    color: colors.primaryApplicationText,
                                                                    mb: 2,
                                                                    lineHeight: '1.5',
                                                                }}
                                                            >
                                                                {replacePlaceholders(question.questionId, question.questionText, variables)}
                                                            </Typography>

                                                            {/* Render Question Based on Type */}
                                                            {question.questionType === 'SCQ' && renderSCQ(question)}
                                                            {question.questionType === 'SLIDE' && renderSCQWithEnhancedSlider(question)}
                                                            {question.questionType === 'MCQ' && renderMCQ(question)}
                                                            {question.questionType === 'DROPDOWN' && renderDropdown(question)}
                                                            {question.questionType === 'INPUT' && renderInput(question)}
                                                        </motion.div>
                                                    );
                                                })}
                                            </motion.div>
                                        ))}

                                        {/* Progress Bar and Controls */}
                                        <Box
                                            sx={{
                                                mt: 'auto', // Push to the bottom of the container
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 2,
                                                width: '100%',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    mt: 'auto', // Push to the bottom of the container
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    width: '100%',
                                                }}
                                            >
                                                {/* Progress Bar */}
                                                <Box sx={{ width: '80%' }}>
                                                    <Box
                                                        sx={{
                                                            position: 'relative',
                                                            height: '12px', // Slightly taller for better visibility
                                                            backgroundColor: '#e0e0e0', // Light gray background
                                                            borderRadius: '6px', // Rounded edges
                                                            overflow: 'hidden', // Keep the progress bar clean
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                height: '100%',
                                                                width: `${calculateProgress()}%`, // Dynamic progress
                                                                background: `linear-gradient(90deg, ${colors.racineRedDark || '#bc2534'}, ${colors.racineRedLight || '#ff6961'})`, // Gradient
                                                                boxShadow: '0px 0px 10px rgba(255, 105, 97, 0.5)', // Glow effect
                                                                transition: 'width 0.3s ease-in-out', // Smooth animation
                                                            }}
                                                        />
                                                    </Box>
                                                    {/* Percentage Text */}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            textAlign: 'center',
                                                            mt: 1, // Spacing below the progress bar
                                                            fontWeight: 'bold',
                                                            fontSize: '1rem', // Slightly larger text
                                                            color: colors.racineRedDark || '#bc2534', // Match theme color
                                                            transition: 'color 0.3s ease-in-out', // Smooth color transition
                                                        }}
                                                    >
                                                        {currentSectionIndex + 1} of {totalSections} Pages
                                                    </Typography>
                                                </Box>
                                                {/* Navigation Buttons */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: colors.racineRedDark,
                                                            color: 'white',
                                                            padding: '10px 20px',
                                                            borderRadius: '5px',
                                                            '&:hover': {
                                                                backgroundColor: colors.racineRedLight,
                                                            },
                                                            marginRight: '10px',
                                                        }}
                                                        onClick={handlePreviousSection}
                                                        disabled={currentSectionIndex === 0}
                                                    >
                                                        Back
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: colors.racineRedDark,
                                                            color: 'white',
                                                            padding: '10px 20px',
                                                            borderRadius: '5px',
                                                            '&:hover': {
                                                                backgroundColor: colors.racineRedLight,
                                                            },
                                                        }}
                                                        onClick={handleNextSection}
                                                    >
                                                        {isLastSection ? 'Submit' : 'Next'}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>

                        </Box>

                    </motion.div>

                    <ConsentDialog open={consentOpen && !surveyComplete && !surveyInactive} onConsent={handleConsent} />
                    <SelfDescribeDialog
                        open={selfDescribeOpen}
                        questionText={getQuestionText()}
                        value={selfDescribeInput}
                        onClose={handleSelfDescribeClose}
                        onSave={handleSelfDescribeClose}
                        onChange={setSelfDescribeInput}
                    />
                    <SurveyCompletedDialog
                        open={surveyComplete}
                        onClose={handleCloseSurvey}
                        iCmtEnabled={iCmtEnabled}
                        comment={comment}
                        onCommentChange={setComment}
                        onSubmitComment={handleCommentSubmit}
                    />
                    <SurveyInactiveDialog open={surveyInactive && !surveyComplete} onClose={handleCloseSurvey} />
                    <InactivityDialog open={isInactive && !surveyInactive && !surveyComplete} onClose={() => { setIsInactive(false); window.location.reload() }} />
                </Layout>
            }
        </>
    );

};

export default PreviewSurvey;
